import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { TOKEN_STORAGE_KEY, TOKEN_STORAGE_TIME } from "store/api/axiosApi";
import { useAppDispatch } from "store/hooks";
import { useUserData } from "store/slices/authSlice";
import { onSetLastRoute, useIsLogin } from "store/slices/commonSlice";
import { clearSlice } from "utils/HelperFunctions";
import { goToInvalid } from "utils/invalidRoute";
import type { PostLoginRoute } from "utils/postLoginRoute";

export type DeepLinkRoute = {
  clusterId: string;
  loginPath: string;
  postLoginRoute: PostLoginRoute;
};

/**
 * Sends a public deep link to its page inside the portal: straight there for
 * a session in the same cluster, otherwise through login, which resumes the
 * stored route. A null route is an invalid link. Memoize the route.
 */
export const useDeepLinkRedirect = (route: DeepLinkRoute | null) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isLogin = useIsLogin();
  const userData = useUserData();

  useEffect(() => {
    if (!route) {
      goToInvalid(navigate, "link");
      return;
    }

    // Store the canonical destination before either branch. If a seemingly
    // active token expires in the protected route, the login flow can still
    // resume this link instead of falling back to the configured landing page.
    dispatch(onSetLastRoute(route.postLoginRoute));

    const hasToken = Boolean(localStorage.getItem(TOKEN_STORAGE_KEY));
    const accountClusterId = String(
      userData?.data?.accountData?.clusterid || ""
    );
    const hasSameClusterSession =
      isLogin && hasToken && accountClusterId === route.clusterId;

    if (hasSameClusterSession) {
      navigate(
        `${route.postLoginRoute.pathname}${route.postLoginRoute.search || ""}`,
        { replace: true }
      );
      return;
    }

    // A token for another tenant must not be reused for this deep link. Keep
    // the pending route, end the old session, and authenticate in the target
    // cluster instead.
    if (isLogin && (!hasToken || accountClusterId !== route.clusterId)) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(TOKEN_STORAGE_TIME);
      clearSlice(dispatch);
    }

    navigate(route.loginPath, { replace: true });
  }, [dispatch, route, isLogin, navigate, userData]);
};
